import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  downloadCommunityAuditExport,
  useCommunityAudit,
  SUPPLIER_PRICING_REASON_LABELS,
  type CommunityAuditExportSection,
  type CommunityAuditLine,
  type CommunityAuditModel,
} from '../api/communityAudit';
import { Button } from '../components/ui/Button';
import { DataTable } from '../components/ui/DataTable';
import { useToast } from '../hooks/useToast';
import page from './Page.module.css';

function formatQty(value: unknown): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '—';
}

function MtoLinesLink({
  communityId,
  mtoId,
  label,
}: {
  communityId: string;
  mtoId: number | null | undefined;
  label: string;
}) {
  if (mtoId == null) {
    return <span>{label}</span>;
  }
  return (
    <Link
      to={`/mtos/${mtoId}/lines?communityId=${communityId}`}
      style={{ fontWeight: 600, color: 'var(--color-navy)' }}
    >
      {label}
    </Link>
  );
}

function AuditSummaryCard({ label, count }: { label: string; count: number }) {
  const ok = count === 0;
  return (
    <div
      style={{
        border: `1px solid ${ok ? 'rgba(39, 174, 96, 0.25)' : 'rgba(192, 57, 43, 0.35)'}`,
        borderRadius: 8,
        padding: '12px 14px',
        background: ok
          ? 'linear-gradient(135deg, #eafaf1 0%, #d5f5e3 100%)'
          : 'linear-gradient(135deg, #fdecea 0%, #fad7d4 100%)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: ok ? '#1e8449' : '#c0392b',
          opacity: 0.9,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 24,
          fontWeight: 700,
          color: ok ? '#1e8449' : '#c0392b',
          marginTop: 2,
        }}
        aria-label={ok ? `${label}: no issues` : `${label}: ${count.toLocaleString()} issues`}
      >
        {ok ? (
          <span aria-hidden="true" style={{ fontSize: 28, lineHeight: 1 }}>
            ✓
          </span>
        ) : (
          count.toLocaleString()
        )}
      </div>
    </div>
  );
}

function IconExportExcel() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="11" height="10" rx="1" />
      <path d="M3 7h11M3 10h11M7 3v10M10 3v10" />
      <path d="M17 11v8M14 16l3 3 3-3" />
    </svg>
  );
}

function AuditSection({
  title,
  description,
  count,
  emptyMessage,
  exportSection,
  communityId,
  communityName,
  children,
}: {
  title: string;
  description: string;
  count: number;
  emptyMessage: string;
  exportSection?: CommunityAuditExportSection;
  communityId?: string;
  communityName?: string;
  children: ReactNode;
}) {
  const toast = useToast();

  return (
    <section style={{ marginTop: 28 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div>
          <h3 className={page.title} style={{ fontSize: 16, marginBottom: 4 }}>
            {title}{' '}
            <span className={page.muted} style={{ fontWeight: 600, fontSize: 14 }}>
              ({count})
            </span>
          </h3>
          <p className={page.muted} style={{ marginTop: 0, marginBottom: 0 }}>
            {description}
          </p>
        </div>
        {count > 0 && exportSection && communityId && communityName ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              void downloadCommunityAuditExport(communityId, exportSection, communityName).catch((e) =>
                toast(e instanceof Error ? e.message : 'Export failed', true)
              )
            }
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconExportExcel />
              Export to Excel
            </span>
          </Button>
        ) : null}
      </div>
      {count === 0 ? <p className={page.muted}>{emptyMessage}</p> : children}
    </section>
  );
}

export function CommunityAuditTab({
  communityId,
  communityName,
}: {
  communityId: string;
  communityName: string;
}) {
  const { data, isLoading, isFetching, error } = useCommunityAudit(communityId);

  const modelColumns = useMemo<ColumnDef<CommunityAuditModel, unknown>[]>(
    () => [
      {
        id: 'model',
        header: 'Model',
        cell: ({ row }) => {
          const modelId = row.original.model_id;
          const name = row.original.model_name;
          if (modelId && name) {
            return (
              <Link to={`/models/${modelId}`} style={{ color: 'var(--color-navy)', fontWeight: 600 }}>
                {String(name)}
              </Link>
            );
          }
          return name ? String(name) : '—';
        },
      },
      {
        id: 'mto',
        header: 'Community MTO',
        cell: ({ row }) => {
          const mtoId = row.original.mto_id;
          const label = row.original.mto_name ? String(row.original.mto_name) : '—';
          if (mtoId == null) return <span className={page.muted}>{label}</span>;
          return (
            <MtoLinesLink communityId={communityId} mtoId={Number(mtoId)} label={label} />
          );
        },
      },
      {
        id: 'catalog_lines',
        header: 'Catalog Lines',
        accessorKey: 'catalog_line_count',
        meta: { align: 'right' },
        cell: ({ getValue }) => {
          const v = getValue();
          return v == null ? '—' : String(v);
        },
      },
      {
        id: 'community_lines',
        header: 'Community Lines',
        accessorKey: 'line_count',
        meta: { align: 'right' },
        cell: ({ getValue }) => String(getValue() ?? 0),
      },
      {
        id: 'issue',
        header: 'Issue',
        accessorKey: 'issue',
        cell: ({ getValue }) => String(getValue() ?? 'Missing MTO lines'),
      },
    ],
    [communityId]
  );

  const unmatchedColumns = useMemo<ColumnDef<CommunityAuditLine, unknown>[]>(
    () => [
      {
        id: 'mto',
        header: 'MTO',
        cell: ({ row }) => (
          <MtoLinesLink
            communityId={communityId}
            mtoId={Number(row.original.mtoId)}
            label={String(row.original.mtoName ?? 'MTO')}
          />
        ),
      },
      {
        id: 'model',
        header: 'Model',
        cell: ({ row }) => (row.original.modelName ? String(row.original.modelName) : '—'),
      },
      { id: 'item', header: 'Item ID', accessorKey: 'itemId', cell: ({ getValue }) => String(getValue() ?? '—') },
      {
        id: 'taskGroup',
        header: 'Payable Task Group',
        accessorKey: 'payableTaskGroup',
        cell: ({ getValue }) => String(getValue() ?? '—'),
      },
      {
        id: 'qty',
        header: 'Qty',
        accessorKey: 'qty',
        meta: { align: 'right' },
        cell: ({ getValue }) => formatQty(getValue()),
      },
    ],
    [communityId]
  );

  const pricingColumns = useMemo<ColumnDef<CommunityAuditLine, unknown>[]>(
    () => [
      {
        id: 'mto',
        header: 'MTO',
        cell: ({ row }) => (
          <MtoLinesLink
            communityId={communityId}
            mtoId={Number(row.original.mtoId)}
            label={String(row.original.mtoName ?? 'MTO')}
          />
        ),
      },
      {
        id: 'model',
        header: 'Model',
        cell: ({ row }) => (row.original.modelName ? String(row.original.modelName) : '—'),
      },
      { id: 'item', header: 'Item ID', accessorKey: 'itemId', cell: ({ getValue }) => String(getValue() ?? '—') },
      {
        id: 'taskGroup',
        header: 'Payable Task Group',
        accessorKey: 'payableTaskGroup',
        cell: ({ getValue }) => String(getValue() ?? '—'),
      },
      {
        id: 'supplier',
        header: 'Supplier',
        cell: ({ row }) => {
          const supplier = row.original.supplier;
          const category = row.original.supplierCategory;
          if (supplier) return String(supplier);
          if (category) return `— (${category})`;
          return '—';
        },
      },
      {
        id: 'reason',
        header: 'Issue',
        accessorKey: 'reason',
        cell: ({ getValue }) => SUPPLIER_PRICING_REASON_LABELS[String(getValue() ?? '')] ?? String(getValue() ?? '—'),
      },
    ],
    [communityId]
  );

  if (isLoading && !data) {
    return (
      <div className={page.card}>
        <p className={page.muted}>Loading audit…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={page.card}>
        <p style={{ color: 'var(--color-danger)' }}>
          {error instanceof Error ? error.message : 'Could not load audit'}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const totalIssues =
    data.summary.modelsWithoutLines + data.summary.unmatchedLines + data.summary.missingSupplierPricing;

  return (
    <div className={page.card}>
      <h2 className={page.title}>Audit</h2>
      <p className={page.muted} style={{ marginBottom: 16 }}>
        Checks that commonly block accurate bid pricing. Fix these before building or exporting a bid.
        {isFetching ? ' Refreshing…' : null}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <AuditSummaryCard label="Total Issues" count={totalIssues} />
        <AuditSummaryCard label="Models Without Lines" count={data.summary.modelsWithoutLines} />
        <AuditSummaryCard label="Unmatched Lines" count={data.summary.unmatchedLines} />
        <AuditSummaryCard label="Missing Supplier Pricing" count={data.summary.missingSupplierPricing} />
      </div>

      <AuditSection
        title="Models Without MTO Lines"
        description="On-site models assigned to this community with no catalog MTO data, no community MTO file, or zero lines in the community MTO. Also includes uploaded MTO files with no lines."
        count={data.summary.modelsWithoutLines}
        emptyMessage="Every assigned model and community MTO has line data."
        exportSection="models-without-lines"
        communityId={communityId}
        communityName={communityName}
      >
        <DataTable data={data.modelsWithoutLines} columns={modelColumns} enableSorting />
      </AuditSection>

      <AuditSection
        title="Unmatched Item Master Lines"
        description="MTO lines whose Item ID does not resolve to an active master item (including aliases and MTO name mappings)."
        count={data.summary.unmatchedLines}
        emptyMessage="All MTO lines match the item master."
        exportSection="unmatched-lines"
        communityId={communityId}
        communityName={communityName}
      >
        <DataTable data={data.unmatchedLines} columns={unmatchedColumns} enableSorting />
      </AuditSection>

      <AuditSection
        title="Missing Supplier Pricing"
        description="Material, equipment, and misc lines that need a supplier sheet price. Labor lines are checked separately on the Costs tab."
        count={data.summary.missingSupplierPricing}
        emptyMessage="All material lines have supplier pricing where required."
        exportSection="missing-supplier-pricing"
        communityId={communityId}
        communityName={communityName}
      >
        <DataTable data={data.missingSupplierPricing} columns={pricingColumns} enableSorting />
      </AuditSection>
    </div>
  );
}
